//----------------------------------
// Imports
//----------------------------------
import { Store } from '@/store';
import Toast from '@/components/ui/Toast';
import * as Sentry from '@sentry/vue';
import i18n from '@/i18n';
import config from './config';

//----------------------------------
// Constants
//----------------------------------
const { API_DEBUG } = config;

const SAV_AUTH_HEADER = 'Authorization';

const COLOR_GREEN = '#008800';
const COLOR_ORANGE = '#884f0e';
const COLOR_RED = '#880000';

const HTTP_STATUSES = {
  200: { color: COLOR_GREEN, title: 'Success' },
  304: { color: COLOR_ORANGE, title: 'Warning' },
  400: { color: COLOR_RED, title: 'Error' },
  401: { color: COLOR_RED, title: 'Error' },
  403: { color: COLOR_RED, title: 'Error' },
  404: { color: COLOR_RED, title: 'Error' },
};

//----------------------------------
// Internal Methods
//----------------------------------
const internal = {
  fetch: async (method, uri, parameters = null, body = null) => {
    const headers = new Headers({
      Accept: 'application/json',
      ...(Store.auth ? { Authorization: `Bearer ${Store.auth} ${Store.stsid}` } : {}),
    });

    const corsMode = 'same-origin';
    const base = `${window.location.protocol}//${window.location.host}`;
    const url = new URL(`/api${uri}`, base);

    if (parameters !== null) {
      const keys = Object.keys(parameters);
      keys.forEach((key) => {
        url.searchParams.append(key, parameters[key]);
      });
    }
    // eslint-disable-next-line no-return-await
    return await window.fetch(url, { method, headers, credentials: 'include', mode: corsMode, body: body ? JSON.stringify(body) : null });
  },
  convertToFormData: (obj, form, namespace) => {
    const formData = form || new FormData();
    const objectKeys = Object.keys(obj);
    objectKeys.forEach((key) => {
      const formKey = namespace ? `${namespace}[${key}]` : key;
      if (obj[key] !== null && typeof obj[key] === 'object' && !(obj[key] instanceof File) && !(obj[key] instanceof Blob)) {
        internal.convertToFormData(obj[key], formData, formKey); // if object, but not a File, use recursivity
      } else {
        formData.append(formKey, obj[key]);
      }
    });
    return formData;
  },
  log: (status, details, url, data) => {
    if (API_DEBUG) {
      const { title, color } = HTTP_STATUSES[status] || { title: 'Unknown', color: COLOR_RED };
      // eslint-disable-next-line no-console
      console.log(
        `%c SAV API ${title} %c ${details} - %c${url}`,
        `background-color:${color};color:#fff;border-radius:0.5em;`,
        'background:inherit;color:inherit;',
        'background:#333;color:#aaa;padding:0 5px;font-style:italic;border-radius:0.1em;',
        data,
      );
    }
  },
};

export default {
  get: async (uri, parameters = null) => {
    let response;
    let responseText = '';
    try {
      response = await internal.fetch('GET', uri, parameters);
      if (response.ok) {
        Store.auth = response.headers.get(SAV_AUTH_HEADER) || null;

        responseText = await response.text();
        return JSON.parse(responseText);
      }
      if (!response.ok && response.status > 400 && response.status <= 511) {
        const errResp = await response.json();
        if (errResp.error) {
          Toast.error(i18n.t(`errors.${errResp.error}`));
        }

        Sentry.captureMessage(`API Error: GET ${uri} (${response.status})`, {
          level: 'error',
          extra: {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            parameters,
            errorResponse: errResp,
          },
        });
      }
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setContext('response', {
          status: response.status,
          url: response.url,
          type: response.type,
        });
        scope.setExtras({
          statusText: response.statusText,
          parameters,
          responseText,
        });
        Sentry.captureException(err);
      });
    }
    Store.clear();
    return null;
  },

  post: async (uri, parameters = null, data = null) => {
    let response;
    let responseText = '';
    try {
      response = await internal.fetch('POST', uri, parameters, data);
      if (response.ok) {
        Store.auth = response.headers.get(SAV_AUTH_HEADER) || null;

        responseText = await response.text();
        return JSON.parse(responseText);
      }
      if (!response.ok && response.status > 400 && response.status <= 511) {
        const errResp = await response.json();
        if (errResp.error) {
          Toast.error(i18n.t(`errors.${errResp.error}`));
        }

        Sentry.captureMessage(`API Error: POST ${uri} (${response.status})`, {
          level: 'error',
          extra: {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            parameters,
            postData: data,
            errorResponse: errResp,
          },
        });
      }
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setContext('response', {
          status: response.status,
          url: response.url,
          type: response.type,
        });
        scope.setExtras({
          statusText: response.statusText,
          parameters,
          postData: data,
          responseText,
        });
        Sentry.captureException(err);
      });
    }
    return null;
  },

  patch: async (uri, parameters = null, data = null) => {
    let response;
    let responseText = '';
    try {
      response = await internal.fetch('PATCH', uri, parameters, data);
      if (response.ok) {
        Store.auth = response.headers.get(SAV_AUTH_HEADER) || null;

        responseText = await response.text();
        return JSON.parse(responseText);
      }
      if (!response.ok && response.status > 400 && response.status <= 511) {
        const errResp = await response.json();
        if (errResp.error) {
          Toast.error(i18n.t(`errors.${errResp.error}`));
        }

        Sentry.captureMessage(`API Error: PATCH ${uri} (${response.status})`, {
          level: 'error',
          extra: {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            parameters,
            postData: data,
            errorResponse: errResp,
          },
        });
      }
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setContext('response', {
          status: response.status,
          url: response.url,
          type: response.type,
        });
        scope.setExtras({
          statusText: response.statusText,
          parameters,
          postData: data,
          responseText,
        });
        Sentry.captureException(err);
      });
    }
    return null;
  },

  delete: async (uri, parameters = null, data = null) => {
    let response;
    let responseText = '';
    try {
      response = await internal.fetch('DELETE', uri, parameters, data);
      if (response.ok) {
        Store.auth = response.headers.get(SAV_AUTH_HEADER) || null;

        responseText = await response.text();
        return JSON.parse(responseText);
      }
      if (!response.ok && response.status > 400 && response.status <= 511) {
        const errResp = await response.json();
        if (errResp.error) {
          Toast.error(i18n.t(`errors.${errResp.error}`));
        }

        Sentry.captureMessage(`API Error: DELETE ${uri} (${response.status})`, {
          level: 'error',
          extra: {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            parameters,
            postData: data,
            errorResponse: errResp,
          },
        });
      }
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setContext('response', {
          status: response.status,
          url: response.url,
          type: response.type,
        });
        scope.setExtras({
          statusText: response.statusText,
          parameters,
          postData: data,
          responseText,
        });
        Sentry.captureException(err);
      });
    }
    return null;
  },


  /*
  upload: (files, model, id, progressHandler = null) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const appBaseUrl = API_BASE_URL === '/' ? '' : APP_BASE_URL;
    xhr.open('POST', `${window.location.origin}${appBaseUrl}/files/`);
    xhr.onload = () => {
      const status = xhr.status;
      if (status >= 200 && status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`${status}: ${xhr.statusText}`));
      }
    };
    xhr.upload.addEventListener('progress', (evt) => {
      if (evt.lengthComputable) {
        progressHandler(evt);
      }
    });
    const filesArr = files.constructor === Array ? files : [files];
    xhr.send(internal.convertToFormData({ files: filesArr, model, id }));
  }),
  */
};
